import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { filterLanguagesByConfig, useLanguages } from '../hooks/useLanguages';
import { useAppConfig } from '../hooks/useAppConfig';
import { useCatalog } from '../hooks/useCatalog';
import LanguageFlag from './LanguageFlag';

const GROUPS = [
  ['Germanic', ['en', 'de', 'nl', 'sv', 'no', 'da']],
  ['Romance', ['es', 'fr', 'it', 'pt', 'ro']],
  ['Slavic & Uralic', ['ru', 'pl', 'cs', 'fi', 'hu']],
  ['Greek & Turkish', ['el', 'tr']],
  ['Middle Eastern', ['ar', 'he', 'fa']],
  ['Indo-Aryan', ['hi', 'bn', 'mr', 'gu', 'pa', 'ur']],
  ['Dravidian', ['ta', 'te', 'kn', 'ml']],
  ['East Asian', ['zh', 'ja', 'ko']],
  ['Southeast Asian', ['vi', 'th', 'id', 'ms', 'fil']],
  ['African', ['sw']],
];

function grouped(languages) {
  const map = Object.fromEntries(languages.map((language) => [language.code, language]));
  return GROUPS.map(([label, codes]) => [label, codes.map((code) => map[code]).filter(Boolean)])
    .filter(([, items]) => items.length);
}

export default function LanguagePreferenceModal() {
  const { t } = useTranslation();
  const {
    setLanguagePreferences,
    closeLanguagePreferences,
    isAuthenticated,
    languagePrefs,
  } = useAuth();
  const { languages, loading: langLoading } = useLanguages();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const { catalog, loading: catLoading } = useCatalog();
  const nativeLanguages = filterLanguagesByConfig(languages, config.nativeLanguageList);
  const configuredTargetLanguages = filterLanguagesByConfig(languages, config.targetLanguageList);
  const savedNative = nativeLanguages.some((language) => language.code === languagePrefs?.nativeLanguage)
    ? languagePrefs.nativeLanguage
    : '';
  const savedTarget = configuredTargetLanguages.some((language) => language.code === languagePrefs?.targetLanguage)
    ? languagePrefs.targetLanguage
    : '';
  const [step, setStep] = useState(savedNative && !savedTarget ? 2 : 1);
  const [nativeLang, setNativeLang] = useState(savedNative);
  const [targetLang, setTargetLang] = useState(savedTarget);
  const [error, setError] = useState('');

  const loading = langLoading || catLoading;
  const targetLanguages = configuredTargetLanguages.filter((l) => l.code !== nativeLang);

  function pickNative(code) {
    setNativeLang(code);
    if (targetLang === code) setTargetLang('');
    setError('');
    setStep(2);
  }

  function handleSubmit() {
    if (!nativeLang) { setError(t('onboarding.mustBeDifferent')); setStep(1); return; }
    if (!targetLang) { setError(t('onboarding.mustBeDifferent')); return; }
    setLanguagePreferences({ nativeLanguage: nativeLang, targetLanguage: targetLang });
  }

  const nativeSel = nativeLanguages.find((l) => l.code === nativeLang);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain bg-black/40 px-4 backdrop-blur-sm"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-preference-title"
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-4">
          <div>
            <p id="language-preference-title" className="text-base font-black text-white">{t('langModal.title')}</p>
            <p className="text-xs text-white/70 mt-0.5">
              {step === 1 ? t('langModal.step1') : t('langModal.step2')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Step indicator dots */}
            <div className="flex gap-1.5">
              <span className={`h-2 w-2 rounded-full transition-all ${step === 1 ? 'bg-white' : 'bg-white/40'}`} />
              <span className={`h-2 w-2 rounded-full transition-all ${step === 2 ? 'bg-white' : 'bg-white/40'}`} />
            </div>
            {/* Close / skip */}
            <button
              type="button"
              onClick={closeLanguagePreferences}
              aria-label={t('langModal.skipForNow')}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white/80 transition hover:bg-white/30 hover:text-white"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-5 py-4">

          {/* ── Step 1: Native language ── */}
          {step === 1 && (
            <>
              <p className="mb-3 text-xs font-bold text-slate-500 uppercase tracking-widest">{t('langModal.iSpeak')}</p>
              {loading ? (
                <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
              ) : (
                <div className="max-h-[min(16rem,45dvh)] space-y-3 overflow-y-auto overscroll-contain pr-1">
                  {grouped(nativeLanguages).map(([group, items]) => <section key={group}>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{group}</p>
                    <div className="grid grid-cols-2 gap-1.5 min-[360px]:grid-cols-3">{items.map((lang) => (
                    <button
                      type="button"
                      key={lang.code}
                      onClick={() => pickNative(lang.code)}
                      className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-center transition ${
                        nativeLang === lang.code
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <LanguageFlag flag={lang.flag} label={lang.label} />
                      <span className="w-full truncate text-xs font-semibold leading-tight">{lang.nativeLabel || lang.label}</span>
                      {lang.nativeLabel && lang.nativeLabel !== lang.label && <span className="w-full truncate text-[9px] text-slate-400">{lang.label}</span>}
                    </button>
                  ))}</div></section>)}
                </div>
              )}
            </>
          )}

          {/* ── Step 2: Target language ── */}
          {step === 2 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('langModal.iLearn')}</p>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline"
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  {nativeSel?.nativeLabel || nativeSel?.label}
                </button>
              </div>

              {loading ? (
                <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
              ) : (
                <div className="max-h-[min(16rem,45dvh)] space-y-3 overflow-y-auto overscroll-contain pr-1">
                  {grouped(targetLanguages).map(([group, items]) => <section key={group}>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{group}</p>
                    <div className="grid grid-cols-2 gap-1.5 min-[360px]:grid-cols-3">{items.map((lang) => {
                    const entry = catalog?.[lang.code];
                    const comingSoon = entry?.features?.length === 0;
                    return (
                      <button
                        type="button"
                        key={lang.code}
                        onClick={() => { setTargetLang(lang.code); setError(''); }}
                        className={`relative flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-center transition ${
                          targetLang === lang.code
                            ? 'border-violet-500 bg-violet-50 text-violet-700'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <LanguageFlag flag={lang.flag} label={lang.label} />
                        <span className="w-full truncate text-xs font-semibold leading-tight">{lang.nativeLabel || lang.label}</span>
                        {lang.nativeLabel && lang.nativeLabel !== lang.label && <span className="w-full truncate text-[9px] text-slate-400">{lang.label}</span>}
                        {comingSoon && (
                          <span className="absolute -top-1 -right-1 rounded-full bg-amber-400 h-2 w-2" title={t('langModal.comingSoon')} />
                        )}
                      </button>
                    );
                  })}</div></section>)}
                </div>
              )}

              {error && <p className="mt-2 text-xs font-semibold text-red-600">{error}</p>}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !targetLang}
                className="mt-4 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-px disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('langModal.letsStart')}
              </button>
            </>
          )}

          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={closeLanguagePreferences}
              className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 transition"
            >
              {t('langModal.skipForNow')}
            </button>
            {!isAuthenticated && (
              <p className="text-[10px] text-slate-400">
                <Link to="/register" className="text-indigo-600 hover:underline">{t('langModal.createAccount')}</Link> {t('langModal.toSync')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
